<script setup lang="ts">
  // Controlled floating panel anchored to an element the caller owns (a row's
  // button, a chip…): one instance can serve every row of a long list instead
  // of one PosUiPopover per row. Positioned `fixed` through
  // use-floating-panel, so it never clips inside scrollers. Focus moves into
  // it on open (unless `autoFocus` is false) and back to the anchor when it
  // closes from inside or on Esc.
  // It closes on a click outside the panel and the anchor (dialogs opened
  // from it do not count), and on its own when the anchor leaves the DOM or
  // stops being visible (the row was filtered out, moved, collapsed or
  // deleted remotely).
  import { computed, nextTick, ref, watch } from 'vue';
  import {
    onClickOutside,
    useEventListener,
    useMutationObserver,
  } from '@vueuse/core';
  import { useFloatingPanel } from './use-floating-panel';

  const props = withDefaults(
    defineProps<{
      value: boolean;
      anchor: HTMLElement | null;
      /** Accessible name of the panel. */
      title: string;
      placement?: 'top' | 'bottom';
      align?: 'start' | 'end';
      /** Panel width (px); never wider than the viewport minus 24px. */
      width?: number;
      /** Gap between the anchor and the panel (px), e.g. room for an arrow. */
      offset?: number;
      /**
       * False: opening leaves the focus where it is (a panel nobody asked
       * for must not take it), and Esc gives it back to the anchor only
       * when it was inside.
       */
      autoFocus?: boolean;
      /** Id of the element that describes the panel (`aria-describedby`). */
      describedBy?: string;
      /**
       * A selector inside the panel that takes the focus when it opens (an
       * input's text is selected); the panel itself otherwise.
       */
      initialFocus?: string;
      /**
       * Changing it while open moves the focus in again (the content was
       * swapped under the same anchor, e.g. a menu entry opening a form).
       */
      focusKey?: string | number;
    }>(),
    {
      placement: 'bottom',
      align: 'start',
      width: 320,
      offset: 4,
      autoFocus: true,
      describedBy: undefined,
      initialFocus: undefined,
      focusKey: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'input', open: boolean): void;
  }>();

  const panelRef = ref<HTMLElement | null>(null);
  const anchorRef = computed(() => props.anchor);
  const open = computed(() => props.value && !!props.anchor);

  const { positioned } = useFloatingPanel(
    anchorRef,
    panelRef,
    open,
    () => `${props.placement}-${props.align}`,
    { onAnchorHidden: () => close(), offset: () => props.offset }
  );

  function focusInside(): boolean {
    const active = document.activeElement;
    return !!panelRef.value && !!active && panelRef.value.contains(active);
  }

  /** Closes; focus goes back to the anchor when it was inside the panel. */
  function close() {
    if (!props.value) return;
    const anchor = props.anchor;
    const restoreFocus = focusInside();
    emit('input', false);
    if (restoreFocus && anchor?.isConnected)
      anchor.focus({ preventScroll: true });
  }

  /** Set on open and on a new anchor; the focus moves in once positioned. */
  let focusPending = false;

  // Immediate: the popover may mount already open.
  watch(
    open,
    (isOpen) => {
      focusPending = isOpen && props.autoFocus;
      if (isOpen && !props.anchor?.isConnected) close();
    },
    { flush: 'post', immediate: true }
  );

  // The panel is invisible until positioned, and invisible elements cannot
  // take the focus: it moves in after the first position lands, so Tab and
  // Esc work from the keyboard.
  async function focusIn(force: boolean) {
    if (!open.value || !positioned.value || !focusPending) return;
    focusPending = false;
    await nextTick();
    const panel = panelRef.value;
    if (!open.value || !panel || (!force && focusInside())) return;
    const target = props.initialFocus
      ? panel.querySelector<HTMLElement>(props.initialFocus)
      : null;
    (target ?? panel).focus({ preventScroll: true });
    if (target instanceof HTMLInputElement) target.select();
  }

  watch(anchorRef, () => {
    if (open.value && props.autoFocus) focusPending = true;
  });

  watch([open, positioned], () => void focusIn(false), { flush: 'post' });

  // New content under the same anchor: its first field takes the focus.
  watch(
    () => props.focusKey,
    () => {
      if (!open.value) return;
      focusPending = true;
      void focusIn(true);
    },
    { flush: 'post' }
  );

  // Closed by its content (a choice, «Guardar», a menu entry): the focus it
  // held goes back to the anchor instead of falling to the page.
  let focusWasInside = false;
  let lastAnchor: HTMLElement | null = null;
  watch(
    open,
    (isOpen, wasOpen) => {
      if (wasOpen && !isOpen) {
        focusWasInside = focusInside();
        lastAnchor = props.anchor;
      }
    },
    { flush: 'pre' }
  );
  watch(open, async (isOpen, wasOpen) => {
    if (!wasOpen || isOpen || !focusWasInside) return;
    focusWasInside = false;
    const anchor = lastAnchor;
    lastAnchor = null;
    await nextTick();
    const active = document.activeElement;
    const lost = !active || active === document.body;
    if (lost && anchor?.isConnected) anchor.focus({ preventScroll: true });
  });

  // Dialogs opened from the panel (vue-js-modal mounts them in
  // `.vm--container`) are not outside clicks.
  onClickOutside(panelRef, () => close(), {
    ignore: [anchorRef, '.vm--container'],
  });

  useEventListener(document, 'keydown', (event: KeyboardEvent) => {
    if (!open.value || event.key !== 'Escape') return;
    event.stopPropagation();
    const anchor = props.anchor;
    // close() already gives back a focus that was inside.
    close();
    if (props.autoFocus && anchor?.isConnected)
      anchor.focus({ preventScroll: true });
  });

  // Only observed while open: `document.body` is the only ancestor sure to
  // outlive the anchor.
  useMutationObserver(
    computed(() => (open.value ? document.body : null)),
    () => {
      if (props.anchor && !props.anchor.isConnected) close();
    },
    { childList: true, subtree: true }
  );
</script>

<template>
  <div
    v-if="open"
    ref="panelRef"
    class="pos-ui-anchored-popover"
    :class="{ 'is-unpositioned': !positioned }"
    :style="{ width: `${width}px` }"
    role="dialog"
    :aria-label="title"
    :aria-describedby="describedBy"
    tabindex="-1"
  >
    <slot :close="close" />
  </div>
</template>

<style scoped lang="scss">
  .pos-ui-anchored-popover {
    // z-30: above the canvas page head (z-20).
    @apply tw-fixed tw-z-30 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-p-3 tw-shadow-lg tw-outline-none;
    max-width: calc(100vw - 24px);
    max-height: calc(100vh - 16px);

    // Invisible (but measurable) until the first position lands.
    &.is-unpositioned {
      @apply tw-invisible;
    }
  }
</style>
